import React, { useState } from "react";
import { Download } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useLanguage } from "@/contexts/LanguageContext";
import { BasicDialog } from "@/components/ui/dialogs/BasicDialog";

/**
 * PWA Install Button for Landing page navigation.
 * Uses shared usePWAInstall hook and native BasicDialog for instructions.
 */
const PWAInstallButton: React.FC = () => {
  const { t } = useLanguage();
  const { isInstalled, canInstall, triggerInstall, getInstructions, deviceType } = usePWAInstall();
  const [showInstructions, setShowInstructions] = useState(false);

  const handleInstallClick = async () => {
    console.log("[PWA] InstallBtn clicked - canInstall:", canInstall, "isInstalled:", isInstalled, "deviceType:", deviceType);
    
    // If native prompt is available, use it
    if (canInstall) {
      console.log("[PWA] Triggering native install...");
      const success = await triggerInstall();
      console.log("[PWA] Install result:", success);
      return;
    }

    // Otherwise show manual instructions
    console.log("[PWA] No native prompt, showing instructions dialog");
    setShowInstructions(true);
  };

  // Don't show if already installed OR on desktop
  if (isInstalled || deviceType === "desktop") return null;

  const instructions = getInstructions();
  
  // Get localized instructions
  const getLocalizedInstructions = () => {
    switch (deviceType) {
      case "ios":
        return {
          title: t("settings.appInstallation.iosTitle"),
          steps: [
            t("settings.appInstallation.iosStep1"),
            t("settings.appInstallation.iosStep2"),
            t("settings.appInstallation.iosStep3"),
          ],
        };
      case "android":
        return {
          title: t("settings.appInstallation.androidTitle"),
          steps: [
            t("settings.appInstallation.androidStep1"),
            t("settings.appInstallation.androidStep2"),
            t("settings.appInstallation.androidStep3"),
          ],
        };
      default:
        return {
          title: t("settings.appInstallation.desktopTitle"),
          steps: [
            t("settings.appInstallation.desktopStep1"),
            t("settings.appInstallation.desktopStep2"),
            t("settings.appInstallation.desktopStep3"),
          ],
        };
    }
  };

  const localizedInstructions = getLocalizedInstructions();

  return (
    <>
      <button
        onClick={handleInstallClick}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200"
        title={t("settings.appInstallation.installButton")}
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">{t("settings.appInstallation.installButton")}</span>
      </button>

      {/* Install Instructions Dialog - M3 compliant BasicDialog */}
      <BasicDialog
        open={showInstructions}
        onOpenChange={setShowInstructions}
        title={localizedInstructions.title}
        dismiss={{ label: t("common.close") }}
      >
        <ol className="text-sm text-muted-foreground space-y-3">
          {localizedInstructions.steps.map((step, index) => (
            <li key={index} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </BasicDialog>
    </>
  );
};

export default PWAInstallButton;
